import { Fragment } from 'react';

// Split a formatted amount so the thousands separators and the decimal point are captured as their own
// tokens (the capturing group keeps the delimiters in the result).
const SEPARATOR = /([.,])/;

interface AmountProps {
	/**
	 * The already-formatted amount to display, e.g. `formatCurrencyAmount(value, 'NGN')` → `"₦1,234.56"`.
	 * Formatting stays with the caller so this works with any currency/decimal style.
	 */
	children?: string | number;
	className?: string;
}

/**
 * Displays a formatted monetary value with its thousands separators and decimal point given extra
 * horizontal room (`.amount_separator`), so the app's tight negative letter-spacing doesn't visually
 * swallow them (`1,234.56` reading like `123456`). The digits keep the global tracking — only the
 * separators are re-spaced — so nothing about the number's look changes except the gaps around `,` and `.`.
 */
const Amount = ({ children, className }: AmountProps) => (
	<span className={className ? `amount ${className}` : 'amount'}>
		{String(children ?? '')
			.split(SEPARATOR)
			.map((part) =>
				part === ',' || part === '.' ? (
					<span
						key={`sep-${part}`}
						className='amount_separator'
					>
						{part}
					</span>
				) : (
					<Fragment key={`text-${part}`}>{part}</Fragment>
				)
			)}
	</span>
);

export default Amount;
